<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import pluralize from 'pluralize';
import { format } from 'date-fns';
import SharedRelationCard from 'src/components/shared/SharedRelationCard.vue';
import SharedCopyableField from 'src/components/shared/SharedCopyableField.vue';
import { formatCurrency } from 'src/utils/currency';
import type { FetchContractResponse } from 'src/api/models/fetchContractResponse';

const props = defineProps<{
  contract: FetchContractResponse;
}>();

const router = useRouter();

type Relation = {
  key: string;
  label: string;
  id: string;
  embeddedData: Record<string, unknown>;
  entityRoute: string | null;
};

type RelationDef = {
  key: string;
  label: string;
  embeddedKey?: 'contract_holder' | 'operator' | 'unit';
  idKey?: 'operator_branch_id' | 'contract_holder_contact_person_id';
  routeOverride?: string;
};

const RELATION_DEFS: RelationDef[] = [
  { key: 'contract_holder', label: 'Contractant', embeddedKey: 'contract_holder', routeOverride: 'organisations-detail'  },
  { key: 'contract_holder_contact_person', label: 'Contact opdrachtgever', idKey: 'contract_holder_contact_person_id', routeOverride: 'organisation-contact-persons-detail' },
  { key: 'unit', label: 'Unit', embeddedKey: 'unit' },
  { key: 'operator', label: 'Leverancier', embeddedKey: 'operator', routeOverride: 'organisations-detail' },
  { key: 'operator_branch', label: 'Locatie adres', idKey: 'operator_branch_id', routeOverride: 'addresses-detail' },
];

const relations = computed<Relation[]>(() => {
  const out: Relation[] = [];
  for (const def of RELATION_DEFS) {
    if (def.embeddedKey) {
      const value = props.contract[def.embeddedKey];
      if (value?.id) {
        out.push({
          key: def.key,
          label: def.label,
          id: value.id,
          embeddedData: value as Record<string, unknown>,
          entityRoute: def.routeOverride ?? resolveEntityRoute(def.key),
        });
      }
    } else if (def.idKey) {
      const id = props.contract[def.idKey];
      if (id) {
        out.push({
          key: def.key,
          label: def.label,
          id,
          embeddedData: {},
          entityRoute: def.routeOverride ?? resolveEntityRoute(def.key),
        });
      }
    }
  }
  return out;
});

function resolveEntityRoute(fieldKey: string): string | null {
  const baseName = fieldKey.replace(/_id$/, '').replace(/_/g, '-');
  const pluralName = pluralize(baseName);
  if (router.hasRoute(`${pluralName}-detail`)) return `${pluralName}-detail`;
  if (router.hasRoute(pluralName)) return pluralName;
  return null;
}

function navigateToRelation(relation: Relation) {
  if (!relation.entityRoute || !router.hasRoute(relation.entityRoute)) return;
  if (relation.entityRoute.endsWith('-detail')) {
    router.push({ name: relation.entityRoute, params: { id: relation.id } });
  } else {
    router.push({ name: relation.entityRoute, query: { uuid: relation.id } });
  }
}

function formatDate(value?: string | null): string {
  if (!value) return '-';
  try {
    return format(new Date(value), 'd MMM yyyy');
  } catch {
    return value;
  }
}
</script>

<template>
  <div class="flex flex-col gap-5" data-testid="contract-detail-readonly">
    <dl class="contract-meta-strip">
      <div class="contract-meta-item">
        <dt class="contract-meta-label">Type</dt>
        <dd data-testid="contract-field-type">{{ contract.type ?? '-' }}</dd>
      </div>
      <div class="contract-meta-item">
        <dt class="contract-meta-label">Periode</dt>
        <dd data-testid="contract-field-period">{{ contract.period ?? '-' }}</dd>
      </div>
      <div class="contract-meta-item">
        <dt class="contract-meta-label">Looptijd</dt>
        <dd class="tabular-nums">
          <span data-testid="contract-field-start-date">{{ formatDate(contract.start_date) }}</span>
          <span class="text-color-secondary px-1">→</span>
          <span data-testid="contract-field-end-date">{{ formatDate(contract.end_date) }}</span>
        </dd>
      </div>
      <div class="contract-meta-item">
        <dt class="contract-meta-label">Referentie (PO nummer) </dt>
        <dd>
          <SharedCopyableField
            v-if="contract.reference_number"
            :value="contract.reference_number"
            data-testid="contract-field-reference"
          />
          <span v-else data-testid="contract-field-reference">-</span>
        </dd>
      </div>
      <div class="contract-meta-item">
        <dt class="contract-meta-label">Jaarlijkse aanpassing</dt>
        <dd data-testid="contract-field-annual-adjustment">{{ contract.annual_price_adjustment ? 'Ja' : 'Nee' }}</dd>
      </div>
      <div v-if="contract.max_number_of_hours != null" class="contract-meta-item">
        <dt class="contract-meta-label">Max uren</dt>
        <dd class="tabular-nums" data-testid="contract-field-max-hours">{{ contract.max_number_of_hours }}</dd>
      </div>
      <div v-if="contract.price_per_hour != null" class="contract-meta-item">
        <dt class="contract-meta-label">Tarief per uur</dt>
        <dd class="tabular-nums" data-testid="contract-field-price-per-hour">{{ formatCurrency(contract.price_per_hour) }}</dd>
      </div>
    </dl>

    <section v-if="relations.length > 0" data-testid="contract-relations">
      <header class="contract-section-header">
        <span>Relaties</span>
      </header>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        <SharedRelationCard
          v-for="relation in relations"
          :key="relation.key"
          :data-testid="`contract-relation-${relation.key}`"
          :label="relation.label"
          :id="relation.id"
          :embedded-data="relation.embeddedData"
          :entity-route="relation.entityRoute"
          :hide-fields="['id']"
          stacked
          @navigate="navigateToRelation(relation)"
        />
      </div>
    </section>

    <section v-if="contract.description">
      <header class="contract-section-header">
        <span>Omschrijving</span>
      </header>
      <div class="text-sm whitespace-pre-wrap" data-testid="contract-field-description">{{ contract.description }}</div>
    </section>
  </div>
</template>

<style scoped>
.contract-meta-strip {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.25rem 1.25rem;
  font-size: 0.875rem;
}

.contract-meta-item {
  display: flex;
  align-items: baseline;
  gap: 0.4rem;
  min-width: 0;
}

.contract-meta-label {
  font-size: 0.6875rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--p-text-muted-color);
  font-weight: 500;
  white-space: nowrap;
}

.contract-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--p-text-muted-color);
  padding-bottom: 0.375rem;
  margin-bottom: 0.5rem;
  border-bottom: 1px solid var(--p-content-border-color);
}
</style>
